import { useCallback, useContext, useEffect, useState, type ChangeEvent, type Key, type MouseEvent, type SetStateAction } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Table } from "antd";
import clsx from "clsx";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { journalsQueries, TitlePageFillersDropdown } from "@/entities/journal";
import { getStatusWithIcon } from "@/entities/journal/helpers/status-map";
import {
  JournalSectionContent,
  JournalSectionDescription,
  JournalSectionFilters,
  JournalSectionPageHeader,
  JournalSectionPagination,
  JournalSectionRecordsToolbar,
  JournalSectionWrapper,
  useJournalSectionPagination,
  JournalRecordPreview,
  type JournalRecordPreviewMeta,
  isTablePreviewClick,
} from "@/entities/journal/ui";
import { JournalSectionNavTitle, useJournalSectionPersonnelPageConfig } from "@/features/journal-section-personnel";
import { ModalInfo } from "@/features/modal-info";
import { useCoordinator, useLocalUser, useNavigationPosition } from "@/shared/lib/hooks";
import { queryClient } from "@/shared/lib/react-query";
import { useDebounce } from "@/shared/utils";
import {
  AbilityContext,
  Can,
  EntityBaseAbilityBuilder,
  useUpdateAbility,
} from "@shared/lib/casl";
import { useAppRoutes } from "@shared/lib/react-router";
import { type IStatus } from "@shared/ui";
import { JournalBulkSendToSignModal } from "../../../journal-bulk-send-to-sign";
import { useAction, ModalStateFirst } from "../../model/hooks/use-action";
import { useRecallSignEntryModal } from "../../model/hooks/use-recall-sign-entry-modal";
import { useRejectSignEntryModal } from "../../model/hooks/use-reject-sign-entry-modal";
import { useSendSignEntryModal } from "../../model/hooks/use-send-sign-entry-modal";
import { useSendSignEntryMultiModal } from "../../model/hooks/use-send-sign-entry-multi-modal";
import { useUpdateStatusToNewEntryModal } from "../../model/hooks/use-update-status-to-new-entry-modal";
import { useSectionOneActions } from "../../model/mutate-hooks/use-section-one-actions";
import { dataSourceConfig } from "../../model/utils/data-source-config";
import { getTableColumns } from "../../model/utils/get-table-columns";
import { mapSectionItemToFormPayload } from "../../model/utils/map-section-ittem-to-form-payload";
import { useSetupSigningString } from "../../model/utils/setup-signing-string";
import { useWithSignedString } from "../../model/utils/with-signed-string";
import { RejectedRecordsCollapsible } from "../rejected-records-collapsible";
import {
  JournalSectionFirstActiveFilterChips,
  JournalSectionFirstFilter,
} from "./journal-section-first-filter/journal-section-first-filter";
import { JournalVersionModal } from "./journal-version-modal/ui";
import "./journals-section-first.scss";

export const JournalsSectionFirst = () => {
  const [modalState, setModalState] = useState<ModalStateFirst>({
    visible: false,
    title: "",
    info: "",
    confirmText: "",
    cancelText: "",
    inputPlaceholder: "",
    inputValue: "",
    isInputModal: false,
    onConfirm: null,
    action: null,
    isDivider: false,
    confirmDisabled: false,
  });
  const [inputError, setInputError] = useState("");

  const { mainJournalId, titleId, projectId, objectId } = useCoordinator();
  const { isInJournalVersionSection } = useNavigationPosition();
  const localUser = useLocalUser();
  const navigate = useNavigate();
  const location = useLocation();
  const appRoutes = useAppRoutes();
  const [searchParams, setSearchParams] = useSearchParams();
  const itemNoVersionId = searchParams.get("itemNoVersionId");
  const rawAction = searchParams.get("action");
  const action = (rawAction || "").toUpperCase();
  const statusFromUrl = searchParams.get("status");

  const isFormOrViewPage = location.pathname.includes("/form/") || location.pathname.includes("/view/");
  const isPersonnelPage = location.pathname.includes("/personnel");

  const [isBulkSendModalOpen, setIsBulkSendModalOpen] = useState(false);
  const [, setSelectedRowKeys] = useState<Key[]>([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [, setIsForm] = useState(
    !!(itemNoVersionId && action === "EDIT"),
  );
  const [, setFormKey] = useState(Date.now());
  const [, setInitialValues] = useState<any>(null);
  const [, setInitUserInfo] = useState<{
    name: string;
    organization: string;
    position: string;
  } | null>(null);

  // Работаем напрямую с URL параметрами через React Router
  const selectedStatus = statusFromUrl
    ? getStatusWithIcon(statusFromUrl)
    : null;

  const setSelectedStatus = (value: SetStateAction<IStatus | null>) => {
    const status = typeof value === "function" ? value(selectedStatus) : value;
    const params = new URLSearchParams(searchParams);

    if (!status) {
      params.delete("status");
    } else {
      params.set("status", status.id);
    }
    setSearchParams(params);
  };
  const [dateStart, setDateStart] = useState<string>("");
  const [dateEnd, setDateEnd] = useState<string>("");
  const [, setVersionId] = useState<string | null>(null);
  const [versionModalItemId, setVersionModalItemId] = useState<string | null>(
    null,
  );
  const [previewRecord, setPreviewRecord] = useState<JournalRecordPreviewMeta | null>(
    null,
  );

  const pagination = useJournalSectionPagination(10);
  const { page, setPage, limit } = pagination;
  const [, setCreateFormTitle] = useState(
    action === "EDIT"
      ? "Редактирование записи"
      : "Добавление записи в раздел 1 ОЖР",
  );

  // Redirect URL params to route-based navigation
  useEffect(() => {
    const id = searchParams.get("itemNoVersionId");
    const act = (searchParams.get("action") || "").toUpperCase();

    if (id && act === "EDIT") {
      navigate(
        appRoutes.journals.section1Form(projectId, objectId, mainJournalId, titleId, id),
      );
      const params = new URLSearchParams(searchParams);
      params.delete("action");
      params.delete("itemNoVersionId");
      setSearchParams(params);
    } else if (id && act === "VIEW") {
      navigate(
        appRoutes.journals.section1View(projectId, objectId, mainJournalId, titleId, id),
      );
      const params = new URLSearchParams(searchParams);
      params.delete("action");
      params.delete("itemNoVersionId");
      setSearchParams(params);
    }
  }, [appRoutes.journals, searchParams, navigate, projectId, objectId, mainJournalId, titleId, setSearchParams]);

  const { data: titleJournal, isLoading: isTitleLoading } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  const { data: mainJournalData } = useQuery(
    journalsQueries.getJournal({ mainJournalId }),
  );

  const { data: sectionData, isFetching } = useQuery({
    ...journalsQueries.getSection1({
      sectionOneId: mainJournalData?.mainJournal?.section1?.sectionId || "",
      search: debouncedSearch,
      page,
      limit,
      status: selectedStatus?.id || "",
      dateFrom: dateStart,
      dateTo: dateEnd,
    }),
    enabled: !!mainJournalData?.mainJournal?.section1?.sectionId,
  });

  const { handleSend } = useSendSignEntryModal(
    mainJournalData?.mainJournal?.section1?.sectionId || "",
    debouncedSearch,
  );

  const { handleSend: handleSendMany } = useSendSignEntryMultiModal({
    sectionOneId: mainJournalData?.mainJournal?.section1?.sectionId || "",
    sectionOneListId:
      mainJournalData?.mainJournal?.section1?.sectionListId || "",
    search: debouncedSearch,
    handleClose: () => {
      setIsBulkSendModalOpen(false);
    },
  });

  const { handleRecall } = useRecallSignEntryModal(
    mainJournalData?.mainJournal?.section1?.sectionId || "",
    debouncedSearch,
  );

  const { handleReject } = useRejectSignEntryModal(
    mainJournalData?.mainJournal?.section1?.sectionId || "",
    debouncedSearch,
  );

  const { handleUpdate } = useUpdateStatusToNewEntryModal({
    search: debouncedSearch,
    sectionOneId: mainJournalData?.mainJournal?.section1?.sectionId || "",
  });

  const { deleteSectionEntry } = useSectionOneActions(
    mainJournalData,
    debouncedSearch,
  );

  const {
    signingString,
    setupSigningString,
    signingItemId,
  } = useSetupSigningString(mainJournalData, debouncedSearch);
  const { withSignedString } = useWithSignedString();

  const signersList = [
    ...(titleJournal?.mainJournal?.titleJournal?.buildingContractor
      ?.representatives ?? []),
    ...(titleJournal?.mainJournal?.titleJournal?.buildingContractor
      ?.constructionControls ?? []),
  ];

  const {
    handleAction,
    handleOpenForm: handleOpenFormOld,
  } = useAction({
    handleSend,
    handleSendMany,
    handleRecall,
    handleReject,
    handleUpdate,
    deleteSectionEntry,
    setModalState,
    setIsForm,
    setFormKey,
    setTitleInit: setCreateFormTitle,
    setInitialValues,
    setInitUserInfo,
    setSelectedRowKeys,
    setSearchParams,
    mainJournalData,
    mapSectionItemToFormPayload,
  });

  const handleEdit = (item: any) => {
    navigate(
      appRoutes.journals.section1Form(projectId, objectId, mainJournalId, titleId, item?.itemNoVersionId),
    );
  };

  const handleView = (item: any) => {
    navigate(
      appRoutes.journals.section1View(projectId, objectId, mainJournalId, titleId, item?.itemNoVersionId),
    );
  };

  const handleOpenForm = (
    status: boolean,
    event?: MouseEvent<HTMLSpanElement>,
    title?: string,
  ) => {
    if (status) {
      navigate(
        appRoutes.journals.section1Form(projectId, objectId, mainJournalId, titleId, "new"),
      );
    } else {
      handleOpenFormOld(status, event, title);
    }
  };

  const handleModalCancel = () => {
    setModalState((prev: ModalStateFirst) => ({ ...prev, visible: false }));
    setInputError("");
  };

  const handleModalConfirm = () => {
    if (modalState.isInputModal && !modalState.inputValue?.trim()) {
      setInputError("Укажите причину отклонения");
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    modalState.onConfirm && modalState.onConfirm();
    setInputError("");
  };

  const onChangeValue = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setModalState((prev: ModalStateFirst) => ({
      ...prev,
      inputValue: e.target.value,
    }));
    if (e.target.value.trim()) {
      setInputError("");
    }
  };

  const dataSource = dataSourceConfig(sectionData);

  const rejectedWithComment = (sectionData?.section1List || []).filter(
    (item: any) => {
      if (item?.status !== "REJECTED") return false;
      // Поддержка массива comments (для обратной совместимости)
      if (item?.comments && Array.isArray(item.comments) && item.comments.length > 0) {
        const validComments = item.comments.filter(
          (comment: any) => {
            const commentText = comment?.commentText ?? comment?.comment;
            return commentText && commentText.toString().trim();
          },
        );
        return validComments.length > 0;
      }
      // Поддержка одиночного comment (для раздела 1)
      return (item?.comment ?? "").toString().trim();
    },
  );

  const columns = getTableColumns({
    mainJournalData,
    localUser,
    signingString,
    setupSigningString,
    withSignedString,
    debouncedSearch,
    queryClient,
    handleEdit,
    handleView,
    handleUpdate,
    deleteSectionEntry,
    signingItemId,
    setVersionId,
    setVersionModalItemId,
    setModalState,
  });


  useUpdateAbility({
    // @ts-expect-error я тебя ненавижу
    permissions: sectionData?.permissions,
    abilityBuilder: EntityBaseAbilityBuilder(),
  });
  const ability = useContext(AbilityContext);

  const { recordsTitle, recordsPath } = useJournalSectionPersonnelPageConfig(1);
  const isAddDisabled = signersList.length === 0;
  const sectionOneId = mainJournalData?.mainJournal?.section1?.sectionId;

  const handleStartBulkSign = () => {
    setIsBulkSendModalOpen(true);
  };

  const resolveBulkSendTable = useCallback(
    (data: unknown) => ({
      rowKey: "key",
      tableClassName: "journals-section-first-table",
      dataSource: dataSourceConfig(
        data as Parameters<typeof dataSourceConfig>[0],
      ),
      columns: getTableColumns({
        bulkSendMode: true,
        mainJournalData,
        localUser,
        signingString,
        setupSigningString,
        withSignedString,
        debouncedSearch,
        queryClient,
        handleEdit,
        handleView,
        handleUpdate,
        deleteSectionEntry,
        signingItemId,
        setVersionId,
        setVersionModalItemId,
        setModalState,
      }),
    }),
    [
      mainJournalData,
      localUser,
      signingString,
      setupSigningString,
      withSignedString,
      debouncedSearch,
      handleEdit,
      handleView,
      handleUpdate,
      deleteSectionEntry,
      signingItemId,
      setModalState,
    ],
  );

  return (
    <>
      {isFormOrViewPage || isPersonnelPage ? (
        <Outlet />
      ) : (
        <>
      <JournalSectionWrapper>
        {!isInJournalVersionSection && !isTitleLoading ? (
          <JournalSectionPageHeader
            banner={
              isAddDisabled ? (
                <>
                  Для добавления записи в Раздел №1 не хватает информации в
                  Титульном листе в блоке{" "}
                  <span className="journal-section-page-header__hint-link">
                    Уполномоченный представитель лица, осуществляющего
                    строительство
                  </span>
                  .{" "}
                  <TitlePageFillersDropdown
                    sectionType="sectionOne"
                    sectionId={sectionOneId}
                  />
                </>
              ) : undefined
            }
            title={
              <JournalSectionNavTitle
                recordsTitle={recordsTitle}
                recordsPath={recordsPath}
                activeTab="records"
              />
            }
            actions={
              <Can I="edit" a="section_1">
                <JournalSectionRecordsToolbar
                  onStartBulkSign={handleStartBulkSign}
                  showBulkActions={!isAddDisabled}
                  addButton={
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={(event) => handleOpenForm(true, event)}
                      disabled={isAddDisabled}
                      style={{
                        backgroundColor: isAddDisabled
                          ? "rgba(119, 119, 119, 1)"
                          : undefined,
                        borderColor: isAddDisabled
                          ? "rgba(119, 119, 119, 1)"
                          : undefined,
                      }}
                    >
                      Добавить запись
                    </Button>
                  }
                />
              </Can>
            }
          />
        ) : (
          <JournalSectionNavTitle
            recordsTitle={recordsTitle}
            recordsPath={recordsPath}
            activeTab="records"
          />
        )}
        <JournalSectionDescription>
          Список инженерно-технического персонала лица, осуществляющего
          строительство, реконструкцию, капитальный ремонт, занятого при
          строительстве, реконструкции, капитальном ремонте объекта капитального
          строительства
        </JournalSectionDescription>
        <JournalSectionFilters>
          <JournalSectionFirstFilter
            setSearch={setSearch}
            setDateStart={setDateStart}
            setDateEnd={setDateEnd}
            dateStart={dateStart}
            dateEnd={dateEnd}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            signersList={signersList}
            onFiltersApply={() => setPage(1)}
          />
        </JournalSectionFilters>
        <JournalSectionContent>
          {rejectedWithComment.length > 0 ? (
            <RejectedRecordsCollapsible
              items={rejectedWithComment}
              editSubject="section_1"
              onReturnToWork={(item) => {
                const values = mapSectionItemToFormPayload(item, mainJournalData);
                setModalState({
                  visible: true,
                  title: "Вернуть запись в работу",
                  info: "Вы действительно хотите вернуть запись в статус NEW?",
                  confirmText: "Вернуть",
                  cancelText: "Отмена",
                  onConfirm: () =>
                    handleUpdate({
                      sectionOneListId: values.section1ListId,
                      itemNoVersionId: values.itemNoVersionId,
                    } as any),
                });
              }}
            />
          ) : null}
          <AbilityContext.Provider value={ability}>
            <JournalSectionFirstActiveFilterChips
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              dateStart={dateStart}
              dateEnd={dateEnd}
              setDateStart={setDateStart}
              setDateEnd={setDateEnd}
              onFilterRemove={() => setPage(1)}
            />
            <Table
              className="journals-section-first-table"
              dataSource={dataSource}
              columns={columns}
              rowClassName={(record: any) =>
                clsx(
                  "journals-section-first-row",
                  record?.rawItem?.status === "REJECTED" && "journals-section-row--rejected",
                  previewRecord?.itemNoVersionId === record?.key &&
                    "journal-section-row--preview-selected",
                )
              }
              onRow={(record: any) => ({
                onClick: (event: MouseEvent<HTMLElement>) => {
                  if (!isTablePreviewClick(event)) return;
                  setPreviewRecord({
                    itemNoVersionId: record.key,
                    sequenceNumber: record.number,
                    status: record.rawItem?.status ?? record.status,
                    isCurrent: record.rawItem?.isCurrent,
                  });
                },
              })}
              pagination={false}
              rowKey="key"
              loading={isFetching}
            />
            <JournalSectionPagination
              pagination={pagination}
              total={sectionData?.total || 0}
            />
          </AbilityContext.Provider>
        </JournalSectionContent>
      </JournalSectionWrapper>
      <ModalInfo
        visible={modalState.visible}
        onConfirm={modalState.onConfirm ? handleModalConfirm : undefined}
        handleAction={handleAction}
        modalState={modalState}
        onCancel={handleModalCancel}
        title={modalState.title}
        Info={modalState.info}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
        inputPlaceholder={modalState.inputPlaceholder}
        onChangeValue={onChangeValue}
        inputValue={modalState.inputValue}
        isInputModal={modalState.isInputModal}
        isDivider={modalState.isDivider}
        confirmDisabled={modalState.confirmDisabled}
        inputError={inputError}
        onValidationError={() => setInputError("Укажите причину отклонения")}
      />
      {versionModalItemId && (
        <JournalVersionModal
          open={!!versionModalItemId}
          onCancel={() => setVersionModalItemId(null)}
          section1ListId={
            mainJournalData?.mainJournal?.section1?.sectionListId || ""
          }
          itemNoVersionId={versionModalItemId}
        />
      )}
      <JournalBulkSendToSignModal
        open={isBulkSendModalOpen}
        onClose={() => setIsBulkSendModalOpen(false)}
        section={1}
        sectionOneId={mainJournalData?.mainJournal?.section1?.sectionId}
        signersList={signersList}
        resolveTable={resolveBulkSendTable}
        onSendBatch={(items) => handleSendMany({ itemsIds: items })}
      />
      <JournalRecordPreview
        sectionNumber={1}
        record={previewRecord}
        onClose={() => setPreviewRecord(null)}
        onOpenRecord={(itemNoVersionId) => {
          navigate(
            appRoutes.journals.section1View(
              projectId,
              objectId,
              mainJournalId,
              titleId,
              itemNoVersionId,
            ),
          );
          setPreviewRecord(null);
        }}
      />
        </>
      )}
    </>
  );
};
